/**
 * Fetching published template designs from the CDN in the admin.
 *
 * Template element trees live on cdn.nudgesmart.com under a content-hashed,
 * immutable key. The admin reads them from there instead of selecting multi-KB
 * JSON columns out of Postgres for every visible card.
 *
 * FALLBACK IS NOT OPTIONAL. A template has no `payloadUrl` until it has been
 * published (POST /publish-templates, or the model onSuccess hook on its next
 * edit). Those read their tree from the database exactly as before, which is what
 * lets the backfill run gradually instead of as a cutover.
 *
 * @see api/helpers/treeCodec.js — the encoder and the key tables
 * @see extensions/nudgesmart-themeapp-extension/assets/treeCodec.js — decoder source
 */

// Side-effect import: the synced decoder is an IIFE that assigns
// window.NudgesmartTreeCodec. Kept in that form because the same file is loaded
// as a plain <script> on the storefront, where a top-level `export` would throw.
import '../components/utils/treeCodec.js';

/**
 * Published designs are immutable — a content-hashed URL always returns the same
 * bytes. So a URL fetched once never needs refetching, for the lifetime of the
 * page. Keyed by URL rather than template id for the same reason: an edited
 * template gets a NEW url, so a stale entry is unreachable rather than wrong.
 */
const designCache = new Map();

/**
 * Fetch and decode one published design.
 *
 * @param {string} payloadUrl
 * @returns {Promise<{template: object, templateConfig: object, templateVersion: string|null}|null>}
 *          null on any failure — callers fall back to the database copy
 */
export function fetchPublishedDesign(payloadUrl) {
  if (!payloadUrl) return Promise.resolve(null);

  if (!designCache.has(payloadUrl)) {
    const request = fetch(payloadUrl, { credentials: 'omit' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const codec = window.NudgesmartTreeCodec;
        if (!codec) throw new Error('NudgesmartTreeCodec not loaded');
        return codec.decodePayload(payload);
      })
      .catch((error) => {
        // Drop the rejected promise so a retry is possible rather than replaying
        // the same failure for the rest of the session.
        designCache.delete(payloadUrl);
        console.warn('[templateDesign] fetch failed, falling back to database:', payloadUrl, error.message);
        return null;
      });

    designCache.set(payloadUrl, request);
  }

  return designCache.get(payloadUrl);
}

/**
 * How many payloads may be in flight (fetch + decode) at once.
 *
 * The cap is about DECODE, not the network. `codec.decodePayload` and the
 * `renderElementTreeToHTML` pass each design triggers downstream both run on the main
 * thread, so a page of twelve cache hits resolving in the same tick is twelve synchronous
 * decodes plus twelve renders in ONE commit — a long task that delays paint of the cards
 * that already arrived. Four keeps the network pipe full (a CDN round trip is far longer
 * than a decode) while leaving gaps for React to paint between waves.
 */
const FETCH_CONCURRENCY = 4;

/**
 * Fetch designs for many records, reporting each one AS IT LANDS.
 *
 * ⚠️ THE CALLBACK IS THE POINT, not a convenience. This used to be a single
 * `Promise.all`, so the caller could not apply anything until the SLOWEST of a dozen CDN
 * round trips finished — the first card's spinner was gated on the twelfth card's network.
 * `onDesign` lets a caller commit each tree the moment it decodes. The returned Map still
 * resolves at the end with everything that succeeded, so the pre-existing
 * `await fetchPublishedDesigns(records)` shape keeps working unchanged.
 *
 * @param {Array<{id: string, payloadUrl?: string}>} records
 * @param {(id: string, design: object|null) => void} [onDesign] - Called EXACTLY ONCE per
 *   record carrying a `payloadUrl`, in completion order. `design` is null when the fetch or
 *   decode failed, which is what lets a caller accumulate the fallback set incrementally.
 * @returns {Promise<Map<string, object>>} keyed by record id; misses are omitted
 */
export async function fetchPublishedDesigns(records, onDesign) {
  const withPayloads = (records || []).filter((r) => r?.payloadUrl);
  if (withPayloads.length === 0) return new Map();

  const designs = new Map();

  // Shared cursor. Safe without a lock: the read, the bounds check and the increment all
  // happen synchronously before the first `await` in each iteration, and JS is
  // single-threaded — no two workers can ever claim the same record.
  let cursor = 0;

  const worker = async () => {
    while (cursor < withPayloads.length) {
      const record = withPayloads[cursor++];
      const design = await fetchPublishedDesign(record.payloadUrl);
      if (design) designs.set(record.id, design);

      if (onDesign) {
        // A subscriber's throw must not kill the pool. It would abandon every record this
        // worker had not reached yet AND reject this function's promise into a caller that
        // has no `.catch` (useTemplateTrees chains only `.then`/`.finally`) — i.e. a
        // half-loaded grid plus an unhandled rejection, from a React setState.
        try {
          onDesign(record.id, design || null);
        } catch (error) {
          console.error('[templateDesign] onDesign callback threw:', error);
        }
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(FETCH_CONCURRENCY, withPayloads.length) }, worker)
  );

  return designs;
}
